<script setup>
// A TABULAÇÃO FEITA NÃO APARECIA EM LUGAR NENHUM DO TICKET.
//
// Relato do clube em 13/10: "a tabulação realizada não aparece nas informações do TKT · esse tkt
// foi finalizado e tabulado pelo agente".
//
// Procede, e a causa é mais larga que a tabulação. A tabulação tem três eixos: CATEGORIA e TIPO
// vivem como etiqueta nativa, e apareciam, porque o painel de etiquetas existe. A CAUSA RAIZ não
// cabe em etiqueta (são 161 valores na planilha do clube) e mora em atributo da conversa. Só que
// a coluna da direita NÃO TEM bloco de atributos da conversa: ela mostra os do CONTATO, e o dos
// atributos da conversa nunca entrou na ordem padrão do painel.
//
// Então a causa raiz era pedida ao agente em todo encerramento, gravada, espelhada, contada em
// quatro relatórios, e nunca devolvida a quem a digitou. O mesmo valia para a jornada, que é
// derivada dela.
//
// Este bloco é de LEITURA. Mudar a tabulação continua sendo pelo caminho que já existe (encerrar
// ou transferir abre o modal), e é de propósito: a causa raiz precisa existir na taxonomia
// importada, e um campo de texto livre aqui produziria a mesma causa escrita de três jeitos, que
// é o mesmo que não ter relatório.
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useFandataTabulacao } from 'dashboard/composables/useFandataTabulacao';
import { dynamicTime } from 'shared/helpers/timeHelper';

const { t } = useI18n();
const currentChat = useMapGetter('getSelectedChat');
const { garantirOpcoes, categoriaAtual, tipoAtual, causaAtual } =
  useFandataTabulacao();

// As opções são a taxonomia do clube e são carregadas uma vez por sessão. Sem elas, não há como
// saber QUAL das etiquetas da conversa é a categoria e qual é o tipo: a lista é a referência.
onMounted(garantirOpcoes);

const atributos = computed(() => ({
  ...(currentChat.value?.additional_attributes || {}),
  ...(currentChat.value?.custom_attributes || {}),
}));

const jornada = computed(() => atributos.value.fandata_jornada || '');

// Quem tabulou por último, do histórico que a gravação mantém (T-268). É o que responde a dúvida
// exata do clube: o atendente tabulou, e o ticket não mostrava sinal nenhum disso.
//
// ⚠️ O histórico só registra quando o valor MUDA. Conversa em que o agente confirmou o palpite da
// IA sem alterar nada não tem entrada, e aí não há autor a mostrar · o que é honesto: ninguém
// mudou. O campo some em vez de atribuir a alguém uma escolha que não foi dele.
const ultimaMudanca = computed(() => {
  const lista = currentChat.value?.additional_attributes
    ?.fandata_causa_raiz_historico;
  if (!Array.isArray(lista) || !lista.length) return null;
  return lista[lista.length - 1];
});

const autor = computed(() => {
  const registro = ultimaMudanca.value;
  if (!registro) return '';
  if (registro.papel === 'bira') return t('FANDATA_TABULACAO.PELO_BIRA');
  return registro.quem || t('FANDATA_TABULACAO.PELA_EQUIPE');
});

const quando = computed(() => {
  const em = ultimaMudanca.value?.em;
  return em ? dynamicTime(em) : '';
});

const linhas = computed(() =>
  [
    { rotulo: t('FANDATA_TABULACAO.CATEGORIA'), valor: categoriaAtual.value },
    { rotulo: t('FANDATA_TABULACAO.TIPO'), valor: tipoAtual.value },
    { rotulo: t('FANDATA_TABULACAO.CAUSA_RAIZ'), valor: causaAtual.value },
    { rotulo: t('FANDATA_TABULACAO.JORNADA'), valor: jornada.value },
  ].filter(linha => Boolean(linha.valor))
);
</script>

<template>
  <div class="flex flex-col gap-2 px-1 py-1 text-sm">
    <p v-if="!linhas.length" class="text-n-slate-11">
      {{ $t('FANDATA_TABULACAO.VAZIO') }}
    </p>
    <template v-else>
      <div
        v-for="linha in linhas"
        :key="linha.rotulo"
        class="flex items-start gap-2"
      >
        <span class="shrink-0 w-28 text-n-slate-11">{{ linha.rotulo }}</span>
        <span class="min-w-0 break-words text-n-slate-12">
          {{ linha.valor }}
        </span>
      </div>
      <p v-if="autor" class="mt-1 text-xs text-n-slate-10">
        {{ $t('FANDATA_TABULACAO.POR', { quem: autor, quando }) }}
      </p>
    </template>
  </div>
</template>
